import React, {
  FunctionComponent,
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { styled } from '@mui/material';

import Navbar from '../navbar/navbarV2';
import NewSidebar from '../sidebar/newSidebar';
import { TrialTopBanner } from '../trial-banner/TrialTopBanner';
import { useTrialBanner } from '../trial-banner/useTrialBanner';
import { Modes } from './Modes';
import SidebarContainer from './sidebarContainer';
import { useAIStudioSidePanelSignals } from './useAIStudioSidePanelSignals';
import { useAIStudio } from 'src/components/global-ai-studio/AIStudioContext';
import SentryErrorBoundary from 'src/components/sentry-error-boundary/SentryErrorBoundary';
import { useNpsSurvey } from 'src/hooks/hooks';
import useMediaQuery from 'src/hooks/useMediaQuery';
import {
  selectIsSidebarExpanded,
  setIsSidebarExpanded,
} from 'src/slices/ui/uiSlice';
import { useAppDispatch } from 'src/store/hooks';
import { breakpoints } from 'src/styles/breakpoints';
import { Routes } from 'src/utils/enums';

interface LayoutProps {
  footer?: ReactNode;
  globalComponent?: ReactNode;
  main: ReactNode;
  navbar?: ReactNode;
  permissions: Set<string>;
  showMainApp?: boolean;
  showSideBar?: boolean;
}

const Layout: FunctionComponent<React.PropsWithChildren<LayoutProps>> = ({
  globalComponent = null,
  main,
  navbar = <Navbar />,
  permissions = new Set(),
  showMainApp,
  showSideBar = false,
}) => {
  const isMobile = useMediaQuery(breakpoints.mobile);
  const location = useLocation();
  const dispatch = useAppDispatch();

  const isSidebarExpanded = useSelector(selectIsSidebarExpanded);

  // AI Studio side-panel mode: this Layout owns the panel's page
  // integration (the panel itself is portaled to document.body by
  // AIStudioProvider so the chat survives route changes, which remount
  // this per-route Layout).
  const { displayMode: aiStudioMode, isOpen: isAIStudioOpen } = useAIStudio();
  const isAIStudioPanelOpen = isAIStudioOpen && aiStudioMode === 'sidePanel';
  const mainContentRef = useRef<HTMLDivElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);

  useAIStudioSidePanelSignals({
    active: isAIStudioPanelOpen,
    shellRef,
    wrapperRef: mainContentRef,
  });

  // Small screens keep the sidebar but start it collapsed to save space
  useEffect(() => {
    if (isMobile) {
      dispatch(setIsSidebarExpanded(false));
    }
  }, [dispatch, isMobile]);

  // Flag to track toggle state of always show/minimize
  const toggleSidebarFlag = useCallback(() => {
    const updatedToggleFlag = !isSidebarExpanded;
    dispatch(setIsSidebarExpanded(updatedToggleFlag));
  }, [dispatch, isSidebarExpanded]);

  const pathName = location.pathname;

  const isEditActionView = pathName === Routes.ACTION_BUILDER_EDIT;
  const isPredictionSettingsDetailView = pathName.startsWith(
    Routes.PREDICTIONS_SETTINGS + '/',
  );

  const isTopBarVisible = !isEditActionView && !isPredictionSettingsDetailView;

  // No route-level navbar above the wrapper on showNavBar: false pages
  // (they render their own inside it), so don't reserve its 69px.
  const hasRouteNavbar = isTopBarVisible && Boolean(navbar);

  useNpsSurvey();
  const sidebarMode = isSidebarExpanded ? Modes.FULL : Modes.MINIMIZED;
  const { banner: trialBanner, onDismiss: onDismissTrialBanner } =
    useTrialBanner();

  return (
    <>
      {globalComponent}
      {showMainApp ? (
        <LayoutShell ref={shellRef}>
          {trialBanner && (
            <TrialTopBanner
              banner={trialBanner}
              onDismiss={onDismissTrialBanner}
            />
          )}
          <AppContainer>
            {showSideBar && (
              <SidebarContainer
                isExpanded={isSidebarExpanded}
                mode={sidebarMode}
              >
                <NewSidebar
                  isFullWidth={sidebarMode === Modes.FULL}
                  isSidebarExpandedToggle={isSidebarExpanded}
                  permissions={permissions}
                  toggleSidebarFlag={toggleSidebarFlag}
                />
              </SidebarContainer>
            )}
            <MainContentContainer
              data-testid='main-content'
              sidebarMode={showSideBar ? sidebarMode : undefined}
            >
              {isTopBarVisible && navbar ? navbar : null}
              <SentryErrorBoundary>
                {/* Class name is used for setting scroll position from child components: */}
                <MainContentWrapper
                  $aiStudioPanel={isAIStudioPanelOpen}
                  $fullHeight={!hasRouteNavbar}
                  className='js-main-content-wrapper'
                  ref={mainContentRef}
                >
                  {main}
                </MainContentWrapper>
              </SentryErrorBoundary>
            </MainContentContainer>
          </AppContainer>
        </LayoutShell>
      ) : null}
    </>
  );
};

// Stacks a full-bleed top banner above the sidebar+content row. Without a
// banner this collapses to exactly the old single-child layout.
const LayoutShell = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const AppContainer = styled('div')`
  background-color: var(--color-slate-100);
  display: flex;
  flex: 1;
  min-height: 0;
  position: relative;
`;

const MainContentContainer = styled('main')<{
  sidebarMode?: Modes;
}>`
  display: flex;
  flex: 1;
  flex-direction: column;
  overflow-y: auto;
  height: 100%;
  ${props =>
    props.sidebarMode === Modes.MINIMIZED
      ? 'margin-left: var(--width-minimized-sidebar);'
      : undefined}
`;

// $aiStudioPanel insets page content for the AI Studio side panel
// (AIStudioModal overlays the freed right column). The transition token is
// shared with the modal's Grow timing (see index.scss).
const MainContentWrapper = styled('div')<{
  $aiStudioPanel?: boolean;
  $fullHeight?: boolean;
}>`
  display: flex;
  flex: 1;
  flex-direction: column;
  isolation: isolate;
  z-index: 0;
  height: ${props => (props.$fullHeight ? '100%' : 'calc(100% - 69px)')};
  overflow-y: auto;
  padding-right: ${props =>
    props.$aiStudioPanel ? 'var(--ai-studio-panel-width)' : 0};
  transition: padding-right var(--ai-studio-panel-slide);
`;

const MemoizedLayout = memo(Layout);

export default MemoizedLayout;
